const CERTIFIED_CONTENT_SUFFIX = /\s*\[certified content[^\]]*\]\s*$/i;
const NO_LABEL_SUFFIX = /\s*(?:\(no label\)|no label)\s*$/i;

export type ClaimsCategoryLoadState = "idle" | "loading" | "loaded" | "error";

export type ClaimsCategoryMembershipReason =
  | "listed"
  | "not-loaded"
  | "load-error"
  | "no-listed-claims"
  | "not-listed";

export interface ClaimsCategoryMembershipResult {
  isValid: boolean;
  reason: ClaimsCategoryMembershipReason;
}

export type CertifiedWeightClaimsValidationReason =
  | "ok"
  | "no-certified-weight-rows"
  | "missing-claims";

export interface CertifiedWeightClaimsValidationResult {
  isValid: boolean;
  reason: CertifiedWeightClaimsValidationReason;
  missingClaimCount: number;
}

export const claimsCategoryKey = (value: unknown): string => {
  let text = String(value || "").trim().replace(/\s+/g, " ");
  text = text.replace(CERTIFIED_CONTENT_SUFFIX, "");
  text = text.replace(NO_LABEL_SUFFIX, "");
  const upper = text.toUpperCase();
  return upper.includes("MATERIALS MATTER") || upper.includes("MATERIAL MATTER")
    ? "MMS"
    : upper;
};

// DAT-501 "tc Product Claims Category": the rule "each value must be present
// on the Seller SC attached" was struck from the validation list, so
// membership is checked against the TC-standard-scoped claims list only.
export const validateClaimsCategoryMembership = (
  value: unknown,
  listedClaims: readonly unknown[],
  loadState: ClaimsCategoryLoadState
): ClaimsCategoryMembershipResult => {
  if (loadState === "error") {
    return { isValid: false, reason: "load-error" };
  }
  if (loadState !== "loaded") {
    return { isValid: false, reason: "not-loaded" };
  }

  const listedKeys = new Set(
    listedClaims.map(claimsCategoryKey).filter(Boolean)
  );
  if (listedKeys.size === 0) {
    return { isValid: false, reason: "no-listed-claims" };
  }

  return listedKeys.has(claimsCategoryKey(value))
    ? { isValid: true, reason: "listed" }
    : { isValid: false, reason: "not-listed" };
};

export const claimsCategoryMembershipError = (
  reason: ClaimsCategoryMembershipReason,
  rawMaterialCode: string,
  value: unknown
): string => {
  switch (reason) {
    case "not-loaded":
      return "Claims Categories are still loading. Please wait before saving.";
    case "load-error":
      return "Claims Categories could not be loaded. Please retry before saving.";
    case "no-listed-claims":
      return `No Claims Category is available for certified raw material ${rawMaterialCode} under the TC standards.`;
    case "not-listed":
      return `Claims Category "${String(value || "").trim()}" does not belong to the TC standards for certified raw material ${rawMaterialCode}. Please select a listed value.`;
    default:
      return "";
  }
};

export const certifiedWeightClaimFromRow = (row: any): string =>
  String(
    row?.tcClaimsCategory ||
      row?.tcStandard ||
      row?.tcProductRawMaterialCodeClaimsCategory ||
      row?.tcProductRawMaterialCodeStandardLabel ||
      ""
  ).trim();

export const certifiedWeightRowsFromResponseData = (data: any): any[] =>
  Array.isArray(data?.response)
    ? data.response
    : Array.isArray(data)
    ? data
    : [];

// Structural check only: rows exist and every persisted row carries a Claims
// Category. Standard membership and the remaining DAT-501 rules are enforced
// by the backend submit validators (the Seller-SC-approval rule was struck).
export const validateCertifiedWeightClaims = (
  rows: readonly any[]
): CertifiedWeightClaimsValidationResult => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      isValid: false,
      reason: "no-certified-weight-rows",
      missingClaimCount: 0,
    };
  }

  const missingClaimCount = rows
    .map(certifiedWeightClaimFromRow)
    .filter((claim) => !claim).length;
  if (missingClaimCount > 0) {
    return { isValid: false, reason: "missing-claims", missingClaimCount };
  }

  return { isValid: true, reason: "ok", missingClaimCount: 0 };
};

export const certifiedWeightClaimsValidationError = (
  result: CertifiedWeightClaimsValidationResult
): string => {
  if (result.isValid) {
    return "";
  }

  if (result.reason === "no-certified-weight-rows") {
    return "Certified-weight Claims Categories could not be verified. Return to Product Information and save every certified product before continuing.";
  }

  return `${result.missingClaimCount} saved certified-weight ${
    result.missingClaimCount === 1 ? "row has" : "rows have"
  } no Claims Category. Return to Product Information, choose a claim for every certified raw material, and save the product again.`;
};
